import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { applicationService, complianceService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';

const ApplicationFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isNew, setIsNew] = useState(false);
  const [formData, setFormData] = useState({
    legal_name: '',
    date_of_birth: '',
    cdl_number: '',
    cdl_state: '',
    cdl_class: 'A',
    cdl_endorsements: '',
    driver_dot_number: '',
    driver_mc_number: '',
    authority_status: '',
    driving_history: [],
    employment_history: [],
    accident_history: [],
    violation_history: [],
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [authorityPath, setAuthorityPath] = useState('');

  useEffect(() => {
    const type = searchParams.get('type');
    const path = searchParams.get('path');

    if (type && path) {
      // New application
      setIsNew(true);
      setAuthorityPath(path);
      setLoading(false);
    } else {
      // Edit existing
      fetchApplication();
    }
  }, [searchParams]);

  const fetchApplication = async () => {
    try {
      const data = await applicationService.getMyApplication();
      setFormData({
        legal_name: data.legal_name || '',
        date_of_birth: data.date_of_birth || '',
        cdl_number: data.cdl_number || '',
        cdl_state: data.cdl_state || '',
        cdl_class: data.cdl_class || 'A',
        cdl_endorsements: data.cdl_endorsements || '',
        driver_dot_number: data.driver_dot_number || '',
        driver_mc_number: data.driver_mc_number || '',
        authority_status: data.authority_status || '',
        driving_history: data.driving_history || [],
        employment_history: data.employment_history || [],
        accident_history: data.accident_history || [],
        violation_history: data.violation_history || [],
      });
      setAuthorityPath(data.authority_path);
    } catch (error) {
      console.error('Failed to fetch application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        // Create new application
        await applicationService.createApplication({
          application_type: 'driver',
          authority_path: authorityPath,
        });
        
        // Update with form data
        await applicationService.updateMyApplication(formData);
        
        // Initialize compliance checklist
        await complianceService.initializeChecklist();
      } else {
        // Update existing
        await applicationService.updateMyApplication(formData);
      }

      alert('Application saved successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save application:', error);
      alert('Failed to save application');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = ['legal_name', 'date_of_birth', 'cdl_number', 'cdl_state'];
    const missingFields = requiredFields.filter(field => !formData[field]);

    if (missingFields.length > 0) {
      alert(`Please complete all required fields: ${missingFields.join(', ')}`);
      return;
    }

    setSaving(true);
    try {
      // Save first if new
      if (isNew) {
        await applicationService.createApplication({
          application_type: 'driver',
          authority_path: authorityPath,
        });
        await applicationService.updateMyApplication(formData);
        await complianceService.initializeChecklist();
      } else {
        await applicationService.updateMyApplication(formData);
      }

      // Submit application
      await applicationService.submitApplication();

      alert('Application submitted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to submit application:', error);
      alert(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-8">Loading application...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <div className="bg-white shadow-md rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Driver Application</h1>

          {/* Authority Path Display */}
          <div className="mb-6 p-4 bg-blue-50 rounded-md">
            <p className="text-sm font-medium text-blue-900" data-testid="authority-path-display">
              Authority Path: {
                authorityPath === 'dispatch_only' ? 'Dispatch-Only' :
                authorityPath === 'company_driver' ? 'Company Driver' :
                'Lease-On'
              }
            </p>
          </div>

          <form className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Legal Name (as on CDL) *
                  </label>
                  <input
                    type="text"
                    name="legal_name"
                    required
                    value={formData.legal_name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="legal-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="date_of_birth"
                    required
                    value={formData.date_of_birth}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="dob-input"
                  />
                </div>
              </div>
            </div>

            {/* CDL Information */}
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-4">CDL Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CDL Number *
                  </label>
                  <input
                    type="text"
                    name="cdl_number"
                    required
                    value={formData.cdl_number}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="cdl-number-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CDL State *
                  </label>
                  <input
                    type="text"
                    name="cdl_state"
                    required
                    value={formData.cdl_state}
                    onChange={handleChange}
                    placeholder="e.g., CA"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="cdl-state-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CDL Class
                  </label>
                  <select
                    name="cdl_class"
                    value={formData.cdl_class}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="cdl-class-select"
                  >
                    <option value="A">Class A</option>
                    <option value="B">Class B</option>
                    <option value="C">Class C</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endorsements
                  </label>
                  <input
                    type="text"
                    name="cdl_endorsements"
                    value={formData.cdl_endorsements}
                    onChange={handleChange}
                    placeholder="e.g., H, N, T"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="cdl-endorsements-input"
                  />
                </div>
              </div>
            </div>

            {/* Dispatch-Only Fields */}
            {authorityPath === 'dispatch_only' && (
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">Authority Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      DOT Number *
                    </label>
                    <input
                      type="text"
                      name="driver_dot_number"
                      value={formData.driver_dot_number}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      data-testid="dot-number-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      MC Number *
                    </label>
                    <input
                      type="text"
                      name="driver_mc_number"
                      value={formData.driver_mc_number}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      data-testid="mc-number-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Authority Status
                    </label>
                    <select
                      name="authority_status"
                      value={formData.authority_status}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      data-testid="authority-status-select"
                    >
                      <option value="">Select Status</option>
                      <option value="active">Active</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-6 border-t">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-300 disabled:opacity-50"
                data-testid="save-draft-btn"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 disabled:opacity-50"
                data-testid="submit-application-btn"
              >
                {saving ? 'Submitting...' : 'Submit Application'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApplicationFormPage;
